import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { PRODUCT_QUANTITY_CONSTRAINTS, Product, ProductSchema } from "../pos"
import { Discount, LegacyCompatibleDiscountSchema } from "./discount"
import {
  PartFunction,
  PartGrade,
  ProductChoice,
  ServiceAction,
  ServiceFeeSnapshot,
} from "./index"

export enum ApprovalStatus {
  ACCEPTED_VIA_RECEPTION = "ACCEPTED_VIA_RECEPTION",
  ACCEPTED_VIA_PROPER_INSPECTION = "ACCEPTED_VIA_PROPER_INSPECTION",
  ACCEPTED_VIA_ADDON_ORDER = "ACCEPTED_VIA_ADDON_ORDER",
  PENDING_VIA_PROPER_INSPECTION = "PENDING_VIA_PROPER_INSPECTION",
  PENDING_VIA_ADDON_ORDER = "PENDING_VIA_ADDON_ORDER",
  DENIED_VIA_PROPER_INSPECTION = "DENIED_VIA_PROPER_INSPECTION",
  DENIED_VIA_ADDON_ORDER = "DENIED_VIA_ADDON_ORDER",
}

export const ACCEPTED_TASK_STATUSES = [
  ApprovalStatus.ACCEPTED_VIA_RECEPTION,
  ApprovalStatus.ACCEPTED_VIA_PROPER_INSPECTION,
  ApprovalStatus.ACCEPTED_VIA_ADDON_ORDER,
]

/**
 * The tasks a dynamic fee (PERCENTAGE / PER_CHANGE) is priced against: what the
 * customer has agreed to, plus what an unanswered addon order is asking them to
 * agree to.
 *
 * Including the pending addon items is what makes the fee shown alongside the
 * addon order the price the customer would pay by accepting it. Once they
 * answer, nothing is pending any more, so the same rule falls back to
 * accepted-only — which is why declining work now lowers the fee instead of
 * leaving the send-time price in place.
 */
export const FEE_BEARING_TASK_STATUSES = [
  ...ACCEPTED_TASK_STATUSES,
  ApprovalStatus.PENDING_VIA_ADDON_ORDER,
]

export interface Task {
  isDone: boolean
  approvalStatus: ApprovalStatus
}

export const UpdateWashTaskSchema = z.object({
  isDone: z.boolean().optional(),
})

export const UpdatePackageTaskSchema = z.object({
  isDone: z.boolean().optional(),
  laborPrice: z
    .number()
    .min(0, "common.validation.priceNotNegative" satisfies TranslationKey)
    .optional(),
})

export const UpdateEvaluatedPartTaskSchema = z.object({
  isDone: z.boolean().optional(),
  newGrade: z.nativeEnum(PartGrade).optional(),
  laborPrice: z
    .number()
    .min(0, "common.validation.priceNotNegative" satisfies TranslationKey)
    .optional(),
})

export const UpdateManualServiceActionTaskSchema =
  UpdateEvaluatedPartTaskSchema.omit({ newGrade: true })

export const UpdateFeeSnapshotSchema = z.object({
  price: z
    .number()
    .positive("common.validation.priceMustBePositive" satisfies TranslationKey)
    .optional(),
})

export const UpdateProductPriceSchema = z.object({
  price: z
    .number()
    .min(0, "common.validation.priceNotNegative" satisfies TranslationKey),
})

export const UpdateProductArticleNumberSchema = z.object({
  articleNumber: z
    .string()
    .min(1, "common.validation.articleNumberRequired" satisfies TranslationKey),
})

export const BatchUpdateProductPricesSchema = z.object({
  productUpdates: z.array(
    z.object({
      productId: z.string(),
      price: z
        .number()
        .min(0, "common.validation.priceNotNegative" satisfies TranslationKey),
    }),
  ),
})

export const AddProductToActionSchema = z.object({
  ean: z.string().nullable(),
  externalId: z.string().nullable().default(null),
  articleNumber: z.string().nullable().default(null),
  acquisitionCost: z.number().nullable().default(null),
  title: z
    .string()
    .min(1, "common.validation.productNameRequired" satisfies TranslationKey),
  price: z
    .number()
    .min(0, "common.validation.priceAtLeastZero" satisfies TranslationKey),
  quantity: z
    .number()
    .int()
    .min(PRODUCT_QUANTITY_CONSTRAINTS.min)
    .max(PRODUCT_QUANTITY_CONSTRAINTS.max)
    .default(PRODUCT_QUANTITY_CONSTRAINTS.min),
  // When true, the product will be marked as needing ordering and a
  // delivery task will be created so the mechanic can track it.
  needsOrdering: z.boolean().default(false),
  expectedDeliveryDate: z.string().nullable().default(null),
  targetType: z.enum(["package", "partAction", "manualAction"]),
  targetId: z.string(),
  // For packages only: which part action within the package to add the product to
  packagePartId: z.string().optional(),
  // For packages only: create a new part action within the package before adding the product
  createPackagePartAction: z
    .object({
      partId: z.guid(),
      action: z.nativeEnum(ServiceAction),
    })
    .optional(),
})

export const DeleteProductFromActionSchema = z.object({
  productId: z.string(),
})

export const BatchUpdateAllTasksIsDoneSchema = z.object({
  isDone: z.boolean(),
  packageTaskIds: z.array(z.string()),
  partTaskIds: z.array(z.string()),
  washSnapshotIds: z.array(z.string()),
  manualServiceActionIds: z.array(z.string()),
})

export const UpdateReceiptSchema = z.object({
  productUpdates: z
    .array(
      z.object({
        productId: z.string(),
        price: z
          .number()
          .min(
            0,
            "common.validation.priceNotNegative" satisfies TranslationKey,
          ),
      }),
    )
    .optional(),
  partActionUpdates: z
    .array(
      z.object({
        partId: z.string(),
        // laborPriceOriginal is the original price before any discount is applied
        laborPriceOriginal: z
          .number()
          .min(0, "common.validation.priceNotNegative" satisfies TranslationKey)
          .optional(),
        // laborPriceDiscount is the discount percentage (0-100)
        // The discounted price (laborPriceAfterDiscount) is calculated automatically
        laborPriceDiscount:
          LegacyCompatibleDiscountSchema.nullable().optional(),
      }),
    )
    .optional(),
  packageUpdates: z
    .array(
      z.object({
        snapshotPackageId: z.guid(),
        laborPrice: z
          .number()
          .min(0, "common.validation.priceNotNegative" satisfies TranslationKey)
          .optional(),
        laborPriceDiscount:
          LegacyCompatibleDiscountSchema.nullable().optional(),
      }),
    )
    .optional(),
  manualActionUpdates: z
    .array(
      z.object({
        manualServiceActionId: z.guid(),
        laborPrice: z
          .number()
          .min(
            0,
            "common.validation.priceNotNegative" satisfies TranslationKey,
          ),
      }),
    )
    .optional(),
  washUpdate: z
    .object({
      washSnapshotId: z.guid(),
      price: z
        .number()
        .min(0, "common.validation.priceNotNegative" satisfies TranslationKey),
    })
    .optional(),
  feeUpdates: z
    .array(
      z.object({
        snapshotId: z.guid(),
        price: z
          .number()
          .positive(
            "common.validation.priceMustBePositive" satisfies TranslationKey,
          ),
      }),
    )
    .optional(),
  deletedFeeSnapshotIds: z.array(z.string()).optional(),
  customerBudget: z
    .number()
    .min(0, "common.validation.budgetNotNegative" satisfies TranslationKey)
    .nullable()
    .optional(),
  orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
})

export type AddProductToAction = z.infer<typeof AddProductToActionSchema>
export type DeleteProductFromAction = z.infer<
  typeof DeleteProductFromActionSchema
>
export type UpdateWashTask = z.infer<typeof UpdateWashTaskSchema>
export type UpdatePackageTask = z.infer<typeof UpdatePackageTaskSchema>
export type UpdateEvaluatedPartTask = z.infer<
  typeof UpdateEvaluatedPartTaskSchema
>
export type UpdateManualServiceActionTask = z.infer<
  typeof UpdateManualServiceActionTaskSchema
>
export type UpdateFeeSnapshot = z.infer<typeof UpdateFeeSnapshotSchema>
export type UpdateProductPrice = z.infer<typeof UpdateProductPriceSchema>
export type BatchUpdateProductPrices = z.infer<
  typeof BatchUpdateProductPricesSchema
>
export type BatchUpdateAllTasksIsDone = z.infer<
  typeof BatchUpdateAllTasksIsDoneSchema
>
export type UpdateReceipt = z.infer<typeof UpdateReceiptSchema>
export type UpdateProductArticleNumber = z.infer<
  typeof UpdateProductArticleNumberSchema
>

const ReplacePartActionSchema = z.object({
  partId: z.guid(),
  action: z.nativeEnum(ServiceAction),
  grade: z.nativeEnum(PartGrade).nullable(),
  laborPrice: z.number().min(0),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  comment: z.string().optional(),
  products: z.array(ProductSchema).default([]),
})

const ReplaceManualServiceActionSchema = z.object({
  manualServiceActionId: z.guid().optional(),
  title: z
    .string()
    .min(1, "common.validation.nameRequired" satisfies TranslationKey),
  comment: z.string().optional(),
  laborPrice: z.number().min(0),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  products: z.array(ProductSchema).default([]),
  customBookingSectionItemId: z.guid().nullable().optional(),
})

const ReplacePackageActionSchema = z.object({
  partId: z.guid(),
  action: z.nativeEnum(ServiceAction),
  comment: z.string().optional(),
  products: z.array(ProductSchema).default([]),
})

const ReplacePackageSchema = z.object({
  snapshotPackageId: z.guid().optional(),
  originalPackageId: z.guid(),
  laborPrice: z.number().min(0),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  actions: z.array(ReplacePackageActionSchema).default([]),
})

const ReplaceFeeSchema = z.object({
  originalFeeId: z.guid(),
  /**
   * The parking fee is priced by the backend; only its discount can be set
   * here, and never below its free day. Ignored on every other fee.
   */
  priceDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
})

export const ReplaceWorkOrderContentsSchema = z.object({
  partActions: z.array(ReplacePartActionSchema),
  washOriginalId: z.guid().nullable(),
  washLaborPriceDiscount:
    LegacyCompatibleDiscountSchema.nullable().default(null),
  manualServiceActions: z.array(ReplaceManualServiceActionSchema),
  packages: z.array(ReplacePackageSchema).default([]),
  fees: z.array(ReplaceFeeSchema).default([]),
  customerBudget: z
    .number()
    .min(0, "common.validation.budgetNotNegative" satisfies TranslationKey)
    .nullable()
    .optional(),
  orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  laborDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  productsDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
})

export type ReplaceWorkOrderContents = z.infer<
  typeof ReplaceWorkOrderContentsSchema
>

export interface EvaluatedPartTask extends Task {
  partId: string
  partName: string
  partImage: string
  action: ServiceAction
  /** Custom label for the action (e.g., for damper service custom names) */
  actionLabel?: string
  /** Null for work agreed at the reception, before the inspection graded it */
  oldGrade: PartGrade | null
  newGrade: PartGrade | null
  /** Whether the component does its job, apart from how worn it is. */
  partFunction: PartFunction
  approvedLaborPrice: number | null
  comment: string
  laborPrice: number | null
  laborPriceAfterDiscount: number | null
  laborPriceDiscount: Discount | null
  packageTaskId: string | null
  isMandatory: boolean
  products: Product[]
  addonProductChoices: ProductChoice[]
  /**
   * "Beror på" dependencies on other items in the same order — currently only
   * populated by the addon-order flow (empty elsewhere). Referenced by stable
   * business keys: parts by partId, manual actions by id, wash by
   * originalWashId.
   */
  requiredWashId: string | null
  requiredPartIds: string[]
  requiredManualServiceActionIds: string[]
}

export interface ManualServiceActionTask extends Task {
  manualServiceActionId: string
  title: string
  comment: string
  isMandatory: boolean
  approvedLaborPrice: number
  laborPrice: number
  laborPriceDiscount: Discount | null
  laborPriceAfterDiscount: number | null
  products: Product[]
  addonProductChoices: ProductChoice[]
}

export interface ServiceWashTask extends Task {
  washSnapshotId: string
  originalWashId: string
  laborPrice: number | null
  laborPriceDiscount: Discount | null
  laborPriceAfterDiscount: number | null
  washName: string
  washDescription: string | null
  packageTaskId: string | null
}

export interface ServicePackageTask extends Task {
  id: string
  snapshotPackageId: string
  originalPackageId: string
  packageName: string
  laborPrice: number
  laborPriceAfterDiscount: number | null
  laborPriceDiscount: Discount | null
  includedMaterialCost: number | null
  washTask: ServiceWashTask | null
  evaluatedPartTasks: EvaluatedPartTask[]
}

export interface WorkOrder {
  evaluatedPartTasks: EvaluatedPartTask[]
  manualServiceActionTasks: ManualServiceActionTask[]
  packageTasks: ServicePackageTask[]
  washTasks: ServiceWashTask[]
  fees: ServiceFeeSnapshot[]
  totalCost: number
}
